import { useMemo } from "react";

import { sample } from "lodash-es";

import { ChatLoadingSpinner } from "src/base-components/Chat/ChatLoadingSpinner";
import { ChatLoadingMode } from "src/base-components/Chat/types";
import { assertUnreachable } from "src/utils/typeUtils";

const loadingTexts = [
  "Analyzing your request...",
  "Crafting a response...",
  "Thinking it through...",
  "Processing data...",
  "Generating insights...",
  "Preparing assistance...",
  "Computing solution...",
  "Decoding your query...",
  "Synthesizing answer...",
  "Formulating response...",
] as const;

const retryingTexts = [
  "Retrying...",
  "Giving it another shot...",
  "Attempting again...",
  "Second try in progress...",
  "Reattempting solution...",
  "Taking another crack at it...",
  "Recalculating response...",
  "Round two, here we go...",
  "Regrouping and retrying...",
  "One more attempt...",
  "Refining and retrying...",
  "Another go at it...",
  "Reconfiguring and retrying...",
  "Back to the drawing board...",
  "Persistence mode: engaged...",
] as const;

const regeneratingText = "Regenerating response...";

const getText = (
  mode: ChatLoadingMode,
  loadingText: string | undefined,
  retryingText: string | undefined,
) => {
  switch (mode) {
    case "loading":
      return loadingText;
    case "retrying":
      return retryingText;
    case "regenerating":
      return regeneratingText;
    default:
      assertUnreachable(mode);
  }
};

type ChatLoadingIndicatorProps = {
  mode: ChatLoadingMode;
  dataLoc?: string;
};

export const ChatLoadingIndicator: React.FC<ChatLoadingIndicatorProps> = ({
  mode,
  dataLoc,
}) => {
  const loadingText = useMemo(() => sample(loadingTexts), []);
  const retryingText = useMemo(() => sample(retryingTexts), []);
  const text = getText(mode, loadingText, retryingText);

  return (
    <li className="flex flex-col gap-y-2" data-loc={dataLoc}>
      <ChatLoadingSpinner text={text ?? ""} />
    </li>
  );
};
